import { escapeHtml } from './admin-ui';

/**
 * The admin screen's dialog shell.
 *
 * MOUNTED TO `document.body`, NOT TO THE ADMIN ROOT. This is the load-bearing
 * detail. `renderAdminScreen` works through exactly two delegated listeners on
 * its root (`click` and `submit`), matching on `[data-action]` / `[data-form]`.
 * A modal appended inside that root would push every one of its own buttons
 * through those same handlers — so a modal's "close" would race the screen's
 * `close`, its form would hit the screen's `onSubmit`, and `pick-pane` inside a
 * dialog would repaint the pane underneath it. Mounting on `body` means
 * propagation never reaches the admin root at all, and each dialog owns its
 * listeners on its own element, the way `screens/profile/title-picker.ts`
 * already does.
 *
 * The body is rendered ONCE. Nothing here re-renders it while it is open:
 * replacing `innerHTML` under a user who is typing destroys focus and caret.
 * Live values are patched through `setField`, never by repainting — the same
 * rule `screens/shop/shop-screen.ts`'s `paintCheckoutReadouts()` follows.
 *
 * The three existing hand-rolled modals (title-picker, send-army-modal,
 * colony-screen) are deliberately NOT retrofitted onto this: they mount into
 * their own hosts, carry bespoke multi-step layouts, and have passing tests.
 */

export interface ModalHandle {
  el: HTMLElement;
  close(): void;
  /**
   * Inline error line. Deliberately not just a toast: `showToast` is a no-op
   * unless `mountToastContainer` ran, so a dialog whose only failure channel
   * is a toast can silently swallow a rejected save.
   */
  setError(message: string | null): void;
  setBusy(busy: boolean): void;
  /** Patch one `[data-field="name"]` text node without touching anything else. */
  setField(name: string, text: string): void;
}

export interface ModalOptions {
  title: string;
  subtitle?: string;
  /** Rendered once, verbatim. */
  body: string;
  size?: 'md' | 'lg';
  onAction?(action: string, el: HTMLElement, handle: ModalHandle): void;
  onSubmit?(kind: string, form: HTMLFormElement, handle: ModalHandle): void;
  onInput?(target: HTMLElement, handle: ModalHandle): void;
  onClose?(): void;
}

export function openModal(options: ModalOptions): ModalHandle {
  let closed = false;
  const previouslyFocused = document.activeElement as HTMLElement | null;

  const el = document.createElement('div');
  el.className =
    'fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('aria-label', options.title);
  el.dataset.adminModal = '';
  el.innerHTML = `
    <div data-modal-panel
      class="popup-enter w-full ${options.size === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-md'} max-h-[88dvh] flex flex-col
             bg-surface border border-border border-t-2 border-t-gold rounded-t-lg sm:rounded-lg">
      <div class="shrink-0 flex items-start justify-between gap-3 px-4 py-3 border-b border-border">
        <div class="min-w-0">
          <h2 class="font-heading text-gold text-sm">${escapeHtml(options.title)}</h2>
          ${options.subtitle ? `<p class="text-[10px] text-text-dim leading-relaxed">${escapeHtml(options.subtitle)}</p>` : ''}
        </div>
        <button type="button" data-action="close" aria-label="Close"
          class="shrink-0 min-w-11 min-h-11 -mr-2 -mt-2 flex items-center justify-center text-xl leading-none text-text-dim hover:text-text touch-manipulation">&times;</button>
      </div>
      <div class="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
        data-modal-body>${options.body}</div>
      <p class="hidden shrink-0 px-4 pb-3 text-xs text-crimson" data-modal-error role="alert"></p>
    </div>`;

  function close(): void {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKeydown);
    el.remove();
    previouslyFocused?.focus?.();
    options.onClose?.();
  }

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') close();
  }

  const handle: ModalHandle = {
    el,
    close,
    setError(message) {
      const node = el.querySelector('[data-modal-error]') as HTMLElement | null;
      if (!node) return;
      node.textContent = message ?? '';
      node.classList.toggle('hidden', !message);
    },
    setBusy(busy) {
      el.querySelectorAll('button[type="submit"]').forEach((b) => {
        (b as HTMLButtonElement).disabled = busy;
      });
    },
    setField(name, text) {
      const node = el.querySelector(`[data-field="${name}"]`);
      if (node) node.textContent = text;
    },
  };

  el.addEventListener('click', (e) => {
    if (e.target === el) {
      close();
      return;
    }
    const target = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
    if (!target) return;
    if (target.dataset.action === 'close') {
      close();
      return;
    }
    options.onAction?.(target.dataset.action as string, target, handle);
  });

  el.addEventListener('submit', (e) => {
    const form = e.target as HTMLFormElement;
    if (!form?.dataset?.form) return;
    e.preventDefault();
    handle.setError(null);
    options.onSubmit?.(form.dataset.form, form, handle);
  });

  if (options.onInput) {
    el.addEventListener('input', (e) => options.onInput?.(e.target as HTMLElement, handle));
  }

  document.addEventListener('keydown', onKeydown);
  document.body.appendChild(el);
  (el.querySelector('input, select, textarea, button') as HTMLElement | null)?.focus();

  return handle;
}
